import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { supabase } from "@/integrations/supabase/client";
import { ProposalsTab } from "@/components/admin/proposals-tab";
import { ReportsTab } from "@/components/admin/reports-tab";
import { CompanySettingsTab } from "@/components/admin/company-settings";
import { LibraryTab } from "@/components/admin/library-tab";
import { ConfirmDialog } from "@/components/admin/confirm-dialog";
import {
  type AdminClient,
  type AdminData,
  type AdminLead,
  type AdminPayment,
  confirmAdminLead,
  createAdminPaymentLink,
  createManualClient,
  deleteAdminLead,
  deleteClient,
  denyAdminLead,
  getAdminBootstrap,
  getAdminData,
  inviteAdminClient,
  updateAdminLead,
  updateClientStatus,
} from "@/lib/admin.functions";

export const Route = createFileRoute("/admin")({
  ssr: false,
  beforeLoad: async ({ location }) => {
    // 1. Must be signed in.
    const { data: userRes } = await supabase.auth.getUser();
    if (!userRes.user) {
      throw redirect({
        to: "/dashboard",
        search: { redirect: location.href },
      });
    }
    // 2. Must have admin role. getAdminBootstrap is auth-protected and
    //    returns { isAdmin } using the has_role() SECURITY DEFINER fn.
    try {
      const res = await getAdminBootstrap();
      if (!res.isAdmin) {
        throw redirect({ to: "/dashboard" });
      }
    } catch (err) {
      // Re-throw redirects, otherwise treat the failure as forbidden.
      if (err && typeof err === "object" && "isRedirect" in err) throw err;
      throw redirect({ to: "/dashboard" });
    }
  },
  component: AdminShell,
  head: () => ({
    meta: [
      { title: "Admin · the Vibing Company®" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

const STATUS_OPTIONS = ["reviewing", "onboarding", "active", "paused", "completed"] as const;
type StatusOpt = typeof STATUS_OPTIONS[number];

function money(cents: number | null | undefined, currency: string | null | undefined) {
  if (cents == null) return "—";
  const sym = (currency ?? "usd").toLowerCase() === "eur" ? "€" : "$";
  return `${sym}${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
}
function date(iso: string | null | undefined) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}



function AdminShell() {
  const fetchAdmin = useServerFn(getAdminData);
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["admin-data"],
    queryFn: () => fetchAdmin(),
    refetchOnWindowFocus: true,
  });
  const [tab, setTab] = useState<
    | "clients"
    | "proposals"
    | "reports"
    | "payments"
    | "leads"
    | "invite"
    | "payment_link"
    | "company"
    | "library"
  >("clients");

  if (isLoading) return <Splash text="Loading admin…" />;
  if (error || !data) {
    return (
      <Splash>
        <p className="text-destructive">Failed to load admin data.</p>
      </Splash>
    );
  }

  const counts = {
    clients: data.clients.length,
    payments: data.payments.length,
    leads: data.leads.length,
  };

  return (
    <div className="min-h-dvh bg-background">
      <main id="main" tabIndex={-1} className="mx-auto max-w-6xl px-6 py-10 md:py-16">

        <div className="flex items-center justify-between">
          <div>
            <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
              the Vibing Company® · Admin
            </p>
            <h1 className="mt-2 font-display text-4xl md:text-5xl tracking-tight text-foreground">
              Operations
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => refetch()}
              className="text-xs text-foreground/60 hover:text-foreground underline underline-offset-4"
            >
              Refresh
            </button>
            <button
              onClick={async () => {
                await supabase.auth.signOut();
                window.location.href = "/";
              }}
              className="text-xs text-foreground/60 hover:text-foreground underline underline-offset-4"
            >
              Sign out
            </button>
          </div>
        </div>

        <div role="tablist" aria-label="Admin sections" className="mt-10 flex gap-1 rounded-full border border-foreground/10 bg-white p-1 w-fit">
          {(
            [
              ["clients", `Clients · ${counts.clients}`],
              ["proposals", "Proposals"],
              ["reports", "Reports"],
              ["payments", `Payments · ${counts.payments}`],
              ["leads", `Requests · ${counts.leads}`],
              ["invite", "Invite client"],
              ["payment_link", "Payment link"],
              ["library", "Library"],
              ["company", "Company"],
            ] as const
          ).map(([k, label]) => (
            <button
              key={k}
              type="button"
              role="tab"
              id={`admin-tab-${k}`}
              aria-selected={tab === k}
              aria-controls="admin-tabpanel"
              onClick={() => setTab(k)}
              className={`rounded-full px-4 py-2 text-xs font-medium tracking-wide tabular-nums [transition-property:background-color,color,transform] duration-200 active:scale-[0.96] ${
                tab === k
                  ? "bg-foreground text-background"
                  : "text-foreground/60 hover:text-foreground"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="mt-8" id="admin-tabpanel" role="tabpanel" aria-labelledby={`admin-tab-${tab}`}>
          {tab === "clients" && <ClientsTab clients={data.clients} onChange={refetch} />}
          {tab === "proposals" && <ProposalsTab />}
          {tab === "reports" && <ReportsTab />}
          {tab === "payments" && <PaymentsTab payments={data.payments} />}
          {tab === "leads" && <LeadsTab leads={data.leads} onChange={refetch} />}
          {tab === "invite" && <InviteClientTab />}
          {tab === "payment_link" && <InviteTab />}
          {tab === "library" && <LibraryTab />}
          {tab === "company" && <CompanySettingsTab />}
        </div>
      </main>

    </div>
  );
}

function InviteClientTab() {
  const invite = useServerFn(inviteAdminClient);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await invite({
        data: {
          email,
          name: name || undefined,
          redirectTo: `${window.location.origin}/reset-password`,
        },
      });
      if ("error" in res) throw new Error(res.error);
      return res;
    },
  });

  const reset = () => {
    mutation.reset();
    setEmail("");
    setName("");
  };

  if (mutation.data) {
    return (
      <div className="rounded-2xl border border-foreground/10 bg-white p-8 max-w-xl">
        <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-emerald-600">
          Invitation sent
        </p>
        <h3 className="mt-3 font-display text-2xl tracking-tight">
          {email} is on the list
        </h3>
        <p className="mt-3 text-sm text-foreground/65">
          {mutation.data.emailSent
            ? "We emailed them a link to set their password and sign in to the dashboard."
            : "Account created, but the set-password email failed to send. Use the password link below or resend."}
        </p>
        <button
          onClick={reset}
          className="mt-6 text-xs text-foreground/60 hover:text-foreground underline underline-offset-4"
        >
          Invite another →
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate();
      }}
      className="rounded-2xl border border-foreground/10 bg-white p-8 max-w-xl space-y-5"
    >
      <div>
        <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
          Invite a client to the dashboard
        </p>
        <h3 className="mt-2 font-display text-2xl tracking-tight">
          Create their account
        </h3>
        <p className="mt-1 text-sm text-foreground/55">
          Provisions a dashboard account and emails a set-password link. Only invited accounts can sign in.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Field label="Client email">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="client@company.com"
            className="w-full rounded-lg border border-foreground/15 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20"
          />
        </Field>
        <Field label="Client name (optional)">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Jane Doe"
            className="w-full rounded-lg border border-foreground/15 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20"
          />
        </Field>
      </div>

      {mutation.isError && (
        <p className="text-sm text-destructive">{(mutation.error as Error).message}</p>
      )}

      <button
        type="submit"
        disabled={mutation.isPending}
        className="w-full rounded-lg bg-foreground text-background px-4 py-3 text-sm font-medium tracking-wide hover:opacity-90 disabled:opacity-50"
      >
        {mutation.isPending ? "Inviting…" : "Send invitation"}
      </button>
    </form>
  );
}

function InviteTab() {
  const create = useServerFn(createAdminPaymentLink);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [planName, setPlanName] = useState("");
  const [amount, setAmount] = useState(""); // dollars
  const [currency, setCurrency] = useState("usd");
  const [cycle, setCycle] = useState<"one_time" | "month" | "year">("month");
  const [copied, setCopied] = useState(false);

  const mutation = useMutation({
    mutationFn: async () => {
      const amountCents = Math.round(parseFloat(amount) * 100);
      const res = await create({
        data: {
          customerEmail: email,
          customerName: name || undefined,
          planName,
          amountCents,
          currency,
          billingCycle: cycle,
        },
      });
      if ("error" in res) throw new Error(res.error);
      return res;
    },
  });

  const reset = () => {
    mutation.reset();
    setEmail("");
    setName("");
    setPlanName("");
    setAmount("");
    setCycle("month");
    setCurrency("usd");
    setCopied(false);
  };

  if (mutation.data) {
    return (
      <div className="rounded-2xl border border-foreground/10 bg-white p-8 max-w-xl">
        <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-emerald-600">
          Payment link ready
        </p>
        <h3 className="mt-3 font-display text-2xl tracking-tight">
          Send this link to {email}
        </h3>
        <div className="mt-5 flex items-stretch gap-2">
          <input
            readOnly
            value={mutation.data.url}
            className="flex-1 rounded-lg border border-foreground/15 bg-foreground/5 px-3 py-2 text-sm font-mono text-foreground/80 focus:outline-none"
            onFocus={(e) => e.currentTarget.select()}
          />
          <button
            onClick={async () => {
              await navigator.clipboard.writeText(mutation.data!.url);
              setCopied(true);
              window.setTimeout(() => setCopied(false), 1500);
            }}
            className="rounded-lg bg-foreground text-background px-4 py-2 text-xs font-medium tracking-wide hover:opacity-90"
          >
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
        <p className="mt-3 text-xs text-foreground/55">
          Stripe link ID: <span className="font-mono">{mutation.data.paymentLinkId}</span>
        </p>
        <button
          onClick={reset}
          className="mt-6 text-xs text-foreground/60 hover:text-foreground underline underline-offset-4"
        >
          Create another →
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate();
      }}
      className="rounded-2xl border border-foreground/10 bg-white p-8 max-w-xl space-y-5"
    >
      <div>
        <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
          Create a custom plan
        </p>
        <h3 className="mt-2 font-display text-2xl tracking-tight">
          Generate a Stripe payment link
        </h3>
        <p className="mt-1 text-sm text-foreground/55">
          Builds a new Stripe product, price, and shareable payment link. Send the URL to the client manually.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Field label="Client email">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="client@company.com"
            className="w-full rounded-lg border border-foreground/15 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20"
          />
        </Field>
        <Field label="Client name (optional)">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Jane Doe"
            className="w-full rounded-lg border border-foreground/15 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20"
          />
        </Field>
      </div>

      <Field label="Plan name">
        <input
          type="text"
          required
          value={planName}
          onChange={(e) => setPlanName(e.target.value)}
          placeholder="Custom retainer · Acme Co"
          className="w-full rounded-lg border border-foreground/15 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20"
        />
      </Field>

      <div className="grid grid-cols-3 gap-4">
        <Field label="Amount">
          <input
            type="number"
            required
            min="0.5"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="2500"
            className="w-full rounded-lg border border-foreground/15 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20"
          />
        </Field>
        <Field label="Currency">
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className="w-full rounded-lg border border-foreground/15 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20"
          >
            <option value="usd">USD</option>
            <option value="eur">EUR</option>
            <option value="gbp">GBP</option>
          </select>
        </Field>
        <Field label="Billing cycle">
          <select
            value={cycle}
            onChange={(e) => setCycle(e.target.value as typeof cycle)}
            className="w-full rounded-lg border border-foreground/15 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20"
          >
            <option value="month">Monthly</option>
            <option value="year">Yearly</option>
            <option value="one_time">One-off</option>
          </select>
        </Field>
      </div>

      {mutation.isError && (
        <p className="text-sm text-destructive">{(mutation.error as Error).message}</p>
      )}

      <button
        type="submit"
        disabled={mutation.isPending}
        className="w-full rounded-lg bg-foreground text-background px-4 py-3 text-sm font-medium tracking-wide hover:opacity-90 disabled:opacity-50"
      >
        {mutation.isPending ? "Creating…" : "Generate payment link"}
      </button>
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
        {label}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

const CLIENT_FILTERS = ["all", "active", "invited", "paused"] as const;
type ClientFilter = typeof CLIENT_FILTERS[number];

function ClientsTab({ clients, onChange }: { clients: AdminClient[]; onChange: () => void }) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<ClientFilter>("all");
  const [sort, setSort] = useState<"recent" | "name">("recent");
  const [creating, setCreating] = useState(false);

  const q = query.trim().toLowerCase();
  const visible = clients
    .filter((c) => {
      if (!q) return true;
      return (
        (c.customer_email ?? "").toLowerCase().includes(q) ||
        (c.product_name ?? "").toLowerCase().includes(q)
      );
    })
    .filter((c) => {
      if (filter === "all") return true;
      const s = (c.client_status ?? c.status ?? "").toLowerCase();
      if (filter === "active") return s === "active" || s === "trialing";
      if (filter === "invited") return s === "invited" || s === "reviewing" || s === "onboarding";
      return s === "paused" || s === "canceled" || s === "past_due";
    })
    .sort((a, b) =>
      sort === "name"
        ? (a.customer_email ?? "").localeCompare(b.customer_email ?? "")
        : +new Date(b.created) - +new Date(a.created),
    );

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <label htmlFor="client-search" className="sr-only">
          Search clients
        </label>
        <input
          id="client-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by email or plan…"
          className="min-w-[14rem] flex-1 rounded-xl border border-foreground/15 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20"
        />
        <div role="group" aria-label="Filter clients" className="flex gap-1">
          {CLIENT_FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              aria-pressed={filter === f}
              onClick={() => setFilter(f)}
              className={`rounded-lg px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] [transition-property:transform,color,background-color] duration-200 active:scale-[0.96] ${
                filter === f
                  ? "bg-foreground text-background"
                  : "text-foreground/50 hover:text-foreground"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
        <label htmlFor="client-sort" className="sr-only">
          Sort clients
        </label>
        <select
          id="client-sort"
          value={sort}
          onChange={(e) => setSort(e.target.value as "recent" | "name")}
          className="rounded-xl border border-foreground/15 bg-white px-3 py-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-foreground/20"
        >
          <option value="recent">Newest first</option>
          <option value="name">A–Z</option>
        </select>
        <button
          type="button"
          onClick={() => setCreating((v) => !v)}
          aria-expanded={creating}
          className="rounded-xl bg-foreground px-4 py-2.5 text-xs font-medium text-background [transition-property:transform,opacity] duration-200 active:scale-[0.96]"
        >
          {creating ? "Close" : "New client"}
        </button>
      </div>

      {creating ? (
        <NewClientForm
          onDone={() => {
            setCreating(false);
            onChange();
          }}
        />
      ) : null}

      {visible.length === 0 ? (
        <Empty
          title={clients.length === 0 ? "No clients yet" : "No matching clients"}
          sub={
            clients.length === 0
              ? "Invited clients and live Stripe subscriptions show up here."
              : "Try a different search or filter."
          }
        />
      ) : (
        <ul className="space-y-3">
          {visible.map((c) => (
            <ClientCard
              key={c.subscription_id ?? `invited-${c.profile_id}`}
              client={c}
              onChange={onChange}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function NewClientForm({ onDone }: { onDone: () => void }) {
  const create = useServerFn(createManualClient);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [status, setStatus] = useState("invited");
  const [sendInvite, setSendInvite] = useState(true);

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await create({
        data: {
          email,
          name: name || undefined,
          status,
          sendInvite,
          redirectTo: `${window.location.origin}/reset-password`,
        },
      });
      if ("error" in res) throw new Error(res.error);
      return res;
    },
    onSuccess: () => {
      setEmail("");
      setName("");
      onDone();
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate();
      }}
      className="rounded-2xl border border-foreground/10 bg-white p-6"
    >
      <h3 className="font-display text-xl tracking-tight">Add a client</h3>
      <p className="mt-1 text-sm text-foreground/55">
        Creates the account and profile. Optionally emails a set-password link.
      </p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="nc-email" className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Email
          </label>
          <input
            id="nc-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-2 w-full rounded-xl border border-foreground/15 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20"
          />
        </div>
        <div>
          <label htmlFor="nc-name" className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Name (optional)
          </label>
          <input
            id="nc-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-2 w-full rounded-xl border border-foreground/15 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20"
          />
        </div>
        <div>
          <label htmlFor="nc-status" className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Status
          </label>
          <select
            id="nc-status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="mt-2 w-full rounded-xl border border-foreground/15 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20"
          >
            {["invited", "onboarding", "reviewing", "active", "paused"].map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <label className="flex items-end gap-2 pb-2 text-sm text-foreground/70">
          <input
            type="checkbox"
            checked={sendInvite}
            onChange={(e) => setSendInvite(e.target.checked)}
            className="size-4"
          />
          Send set-password email
        </label>
      </div>
      {mutation.isError ? (
        <p role="alert" className="mt-3 text-sm text-destructive">
          {(mutation.error as Error).message}
        </p>
      ) : null}
      <button
        type="submit"
        disabled={mutation.isPending}
        className="mt-4 rounded-xl bg-foreground px-5 py-2.5 text-xs font-medium text-background disabled:opacity-50 [transition-property:transform,opacity] duration-200 active:scale-[0.96]"
      >
        {mutation.isPending ? "Creating…" : "Create client"}
      </button>
    </form>
  );
}


function ClientCard({ client, onChange }: { client: AdminClient; onChange: () => void }) {
  const update = useServerFn(updateClientStatus);
  const qc = useQueryClient();
  const [note, setNote] = useState(client.client_status_note ?? "");
  const [editing, setEditing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const removeClient = useServerFn(deleteClient);
  const deleteMut = useMutation({
    mutationFn: async () => {
      const res = await removeClient({
        data: {
          profileId: client.profile_id!,
          confirmEmail: client.customer_email ?? "",
        },
      });
      if ("error" in res) throw new Error(res.error);
      return res;
    },
    onSuccess: () => {
      setConfirmOpen(false);
      qc.invalidateQueries({ queryKey: ["admin-data"] });
      onChange();
    },
  });
  const mutation = useMutation({
    mutationFn: (status: StatusOpt) =>
      update({ data: { profileId: client.profile_id!, status, note: note || null } }).then((r) => {
        if ("error" in r) throw new Error(r.error);
        return r;
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-data"] });
      onChange();
    },
  });

  const currentStatus = (client.client_status as StatusOpt | null) ?? "reviewing";

  const isInvited = client.origin === "invited";

  return (
    <li className="rounded-2xl border border-foreground/10 bg-white p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="font-display text-xl tracking-tight text-foreground">
            {client.product_name ?? client.plan_key?.replace(/_/g, " ") ?? "Subscription"}
          </p>
          <p className="text-sm text-foreground/55 mt-0.5">
            {client.customer_email ?? client.customer_id ?? "—"}
          </p>
          {isInvited ? (
            <p className="text-xs text-foreground/45 mt-1 font-mono tabular-nums">
              {client.quoted_amount_cents != null
                ? `${money(client.quoted_amount_cents, client.quoted_currency)} quoted`
                : "No quote set"}
              {" · awaiting payment"}
            </p>
          ) : (
            <p className="text-xs text-foreground/45 mt-1 font-mono tabular-nums">
              {money(client.amount, client.currency)}
              {client.interval ? ` / ${client.interval}` : ""} · {client.status} ·{" "}
              {client.cancel_at_period_end
                ? `Ends ${date(client.current_period_end)}`
                : `Renews ${date(client.current_period_end)}`}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          {isInvited ? (
            <span className="text-[11px] uppercase tracking-wider font-mono text-sky-600">
              Invited · {client.status}
            </span>
          ) : (
            <span
              className={`text-[11px] uppercase tracking-wider font-mono ${
                client.status === "active" || client.status === "trialing"
                  ? "text-emerald-600"
                  : client.status === "past_due"
                    ? "text-amber-600"
                    : "text-foreground/50"
              }`}
            >
              Stripe: {client.status}
            </span>
          )}
          {isInvited && client.pending_payment_link_url && (
            <a
              href={client.pending_payment_link_url}
              target="_blank"
              rel="noopener"
              className="text-[10px] uppercase tracking-wider text-foreground/55 hover:text-foreground underline underline-offset-4"
            >
              Payment link →
            </a>
          )}
          {client.profile_id && (
            <Link
              to="/admin/preview/$profileId"
              params={{ profileId: client.profile_id }}
              target="_blank"
              rel="noopener"
              className="text-[10px] uppercase tracking-wider text-foreground/55 hover:text-foreground underline underline-offset-4"
            >
              Preview client view →
            </Link>
          )}
        </div>
      </div>



      <div className="mt-5 pt-5 border-t border-foreground/10">
        {!client.profile_id ? (
          <p className="text-xs text-amber-700 bg-amber-500/10 rounded-lg px-3 py-2">
            No matching profile yet — customer needs to sign in once before status can be set.
          </p>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
              Client status
            </span>
            <div className="flex gap-1 rounded-full border border-foreground/10 p-1">
              {STATUS_OPTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => mutation.mutate(s)}
                  disabled={mutation.isPending}
                  className={`relative rounded-full px-3 py-1 text-[11px] tracking-wide [transition-property:background-color,color,transform] duration-200 active:scale-[0.96] before:absolute before:inset-0 before:-m-2 before:content-[''] ${
                    currentStatus === s
                      ? "bg-foreground text-background"
                      : "text-foreground/60 hover:text-foreground"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
            <button
              onClick={() => setEditing((e) => !e)}
              className="text-xs text-foreground/55 hover:text-foreground underline underline-offset-4"
            >
              {editing ? "Hide note" : "Add note"}
            </button>
          </div>
        )}
        {editing && client.profile_id && (
          <div className="mt-3 flex gap-2">
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Note shown to the customer…"
              className="flex-1 rounded-lg border border-foreground/15 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20"
            />
            <button
              onClick={() => mutation.mutate(currentStatus)}
              disabled={mutation.isPending}
              className="rounded-lg bg-foreground text-background px-4 py-2 text-xs font-medium disabled:opacity-50 [transition-property:transform,opacity] duration-200 active:scale-[0.96]"
            >
              Save
            </button>
          </div>
        )}
        {mutation.isError && (
          <p className="mt-2 text-xs text-destructive">{(mutation.error as Error).message}</p>
        )}
      </div>

      {client.profile_id ? (
        <div className="mt-5 flex flex-wrap items-center gap-4 border-t border-foreground/10 pt-4">
          <Link
            to="/admin/clients/$profileId"
            params={{ profileId: client.profile_id }}
            className="text-xs text-foreground/60 underline underline-offset-4 hover:text-foreground"
          >
            Open client
          </Link>
          <Link
            to="/admin/preview/$profileId"
            params={{ profileId: client.profile_id }}
            className="text-xs text-foreground/60 underline underline-offset-4 hover:text-foreground"
          >
            Preview dashboard
          </Link>
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            className="ml-auto text-xs text-destructive underline underline-offset-4"
          >
            Delete
          </button>
          <ConfirmDialog
            open={confirmOpen}
            onOpenChange={setConfirmOpen}
            title="Delete this client?"
            description="The login and profile are removed permanently. Proposals, invoices and payments stay on record and are detached from the account."
            confirmWord={client.customer_email ?? ""}
            confirmLabel="Delete client"
            pending={deleteMut.isPending}
            error={deleteMut.error ? (deleteMut.error as Error).message : null}
            onConfirm={() => deleteMut.mutate()}
          />
        </div>
      ) : null}
    </li>
  );
}

function PaymentsTab({ payments }: { payments: AdminPayment[] }) {
  if (payments.length === 0) {
    return <Empty title="No payments yet" sub="One-off charges from Stripe will show here." />;
  }
  return (
    <ul className="divide-y divide-foreground/10 rounded-2xl border border-foreground/10 bg-white overflow-hidden">
      {payments.map((p) => (
        <li key={p.id} className="flex items-center justify-between gap-4 px-5 py-4">
          <div className="min-w-0">
            <p className="text-sm text-foreground truncate">
              {p.description ?? "Charge"}
            </p>
            <p className="text-xs text-foreground/55 truncate">
              {p.customer_email ?? "—"} · {date(p.created)}
            </p>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <span
              className={`text-[11px] uppercase tracking-wider font-mono ${
                p.status === "succeeded" ? "text-emerald-600" : "text-foreground/50"
              }`}
            >
              {p.status}
            </span>
            <span className="font-mono text-sm text-foreground w-20 text-right">
              {money(p.amount, p.currency)}
            </span>
            {p.receipt_url && (
              <a
                href={p.receipt_url}
                target="_blank"
                rel="noopener"
                className="text-[11px] uppercase tracking-wider text-foreground/50 hover:text-foreground underline underline-offset-4"
              >
                Receipt
              </a>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

function LeadsTab({ leads, onChange }: { leads: AdminLead[]; onChange: () => void }) {
  if (leads.length === 0) {
    return <Empty title="No requests yet" sub="Contact form submissions and AI intake threads land here." />;
  }
  return (
    <ul className="space-y-3">
      {leads.map((l) => (
        <LeadCard key={`${l.source}-${l.id}`} lead={l} onChange={onChange} />
      ))}
    </ul>
  );
}

function LeadCard({ lead, onChange }: { lead: AdminLead; onChange: () => void }) {
  const update = useServerFn(updateAdminLead);
  const del = useServerFn(deleteAdminLead);
  const confirmLead = useServerFn(confirmAdminLead);
  const denyLead = useServerFn(denyAdminLead);
  const qc = useQueryClient();
  const [priceInput, setPriceInput] = useState(
    lead.admin_price_cents != null ? (lead.admin_price_cents / 100).toString() : "",
  );
  const [currency, setCurrency] = useState(lead.admin_currency);
  const [editingPrice, setEditingPrice] = useState(false);

  const ADMIN_KEY = ["admin-data"] as const;

  function patchLead(updater: (l: AdminLead) => AdminLead) {
    const prev = qc.getQueryData<AdminData>(ADMIN_KEY);
    if (!prev) return prev;
    const next: AdminData = {
      ...prev,
      leads: prev.leads.map((l) =>
        l.source === lead.source && l.id === lead.id ? updater(l) : l,
      ),
    };
    qc.setQueryData<AdminData>(ADMIN_KEY, next);
    return prev;
  }

  function removeLead() {
    const prev = qc.getQueryData<AdminData>(ADMIN_KEY);
    if (!prev) return prev;
    const next: AdminData = {
      ...prev,
      leads: prev.leads.filter((l) => !(l.source === lead.source && l.id === lead.id)),
    };
    qc.setQueryData<AdminData>(ADMIN_KEY, next);
    return prev;
  }

  const rollback = (snapshot: AdminData | undefined) => {
    if (snapshot) qc.setQueryData(ADMIN_KEY, snapshot);
  };

  // Confirm = full pipeline: invite client + create payment link + send proposal email.
  const confirmMut = useMutation({
    mutationFn: async () => {
      const r = await confirmLead({
        data: {
          source: lead.source,
          id: lead.id,
          redirectTo: `${window.location.origin}/reset-password`,
        },
      });
      if ("error" in r) throw new Error(r.error);
      return r;
    },
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: ADMIN_KEY });
      return { snapshot: patchLead((l) => ({ ...l, admin_status: "confirmed" })) };
    },
    onError: (_e, _v, ctx) => rollback(ctx?.snapshot),
    onSettled: () => onChange(),
  });

  const denyMut = useMutation({
    mutationFn: async () => {
      const r = await denyLead({ data: { source: lead.source, id: lead.id } });
      if ("error" in r) throw new Error(r.error);
      return r;
    },
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: ADMIN_KEY });
      return { snapshot: patchLead((l) => ({ ...l, admin_status: "denied" })) };
    },
    onError: (_e, _v, ctx) => rollback(ctx?.snapshot),
    onSettled: () => onChange(),
  });

  const priceMut = useMutation({
    mutationFn: async (vars: { priceCents: number | null; currency: string }) => {
      const r = await update({
        data: { source: lead.source, id: lead.id, priceCents: vars.priceCents, currency: vars.currency },
      });
      if ("error" in r) throw new Error(r.error);
      return r;
    },
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: ADMIN_KEY });
      setEditingPrice(false);
      return {
        snapshot: patchLead((l) => ({
          ...l,
          admin_price_cents: vars.priceCents,
          admin_currency: vars.currency,
        })),
      };
    },
    onError: (_e, _v, ctx) => rollback(ctx?.snapshot),
    onSettled: () => onChange(),
  });

  const deleteMut = useMutation({
    mutationFn: async () => {
      const r = await del({ data: { source: lead.source, id: lead.id } });
      if ("error" in r) throw new Error(r.error);
      return r;
    },
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: ADMIN_KEY });
      return { snapshot: removeLead() };
    },
    onError: (_e, _v, ctx) => rollback(ctx?.snapshot),
    onSettled: () => onChange(),
  });

  const handleDelete = () => {
    if (!window.confirm(`Delete this ${lead.source === "intake" ? "AI intake" : "contact"} request? This cannot be undone.`)) return;
    deleteMut.mutate();
  };

  const handleSavePrice = () => {
    const parsed = priceInput.trim() === "" ? null : Math.round(parseFloat(priceInput) * 100);
    if (parsed != null && (!Number.isFinite(parsed) || parsed < 0)) return;
    priceMut.mutate({ priceCents: parsed, currency });
  };

  const handleConfirm = () => {
    if (lead.admin_price_cents == null) {
      window.alert("Set a price before confirming.");
      return;
    }
    const isResend = lead.admin_status === "confirmed";
    const msg = isResend
      ? `Resend the proposal email to ${lead.email}?`
      : `Confirm this request? We'll create an account for ${lead.email}, generate a payment link, and email them the proposal.`;
    if (!window.confirm(msg)) return;
    confirmMut.mutate();
  };

  const handleDeny = () => {
    if (!window.confirm(`Deny this request? We'll email ${lead.email ?? "the requester"} a polite decline.`)) return;
    denyMut.mutate();
  };

  const isConfirmed = lead.admin_status === "confirmed";
  const isDenied = lead.admin_status === "denied";
  const hasPrice = lead.admin_price_cents != null;

  return (
    <li className="rounded-2xl border border-foreground/10 bg-white p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`text-[10px] uppercase tracking-widest font-mono ${
                lead.source === "intake" ? "text-sky-600" : "text-foreground/55"
              }`}
            >
              {lead.source === "intake" ? "AI intake" : "Contact form"}
            </span>
            <span className="text-[10px] text-foreground/40">·</span>
            <span className="text-[10px] text-foreground/45 font-mono">{date(lead.created_at)}</span>
            <span
              className={`text-[10px] uppercase tracking-widest font-mono px-2 py-0.5 rounded-full ${
                isConfirmed
                  ? "bg-emerald-500/10 text-emerald-700"
                  : isDenied
                    ? "bg-foreground/10 text-foreground/60"
                    : "bg-amber-500/10 text-amber-700"
              }`}
            >
              {isConfirmed ? "Confirmed" : isDenied ? "Denied" : "Pending"}
            </span>
            {lead.admin_price_cents != null && (
              <span className="text-[10px] uppercase tracking-widest font-mono px-2 py-0.5 rounded-full bg-foreground/5 text-foreground/70">
                {money(lead.admin_price_cents, lead.admin_currency)}
              </span>
            )}
          </div>
          <p className="mt-2 text-foreground font-medium">
            {lead.name ?? "Anonymous"}{" "}
            <span className="text-foreground/50 font-normal">{lead.email ?? ""}</span>
          </p>
          <p className="mt-1 text-sm text-foreground/65 line-clamp-3">{lead.summary}</p>
          {lead.status && (
            <p className="mt-2 text-[10px] uppercase tracking-widest font-mono text-foreground/55">
              {lead.status}
            </p>
          )}
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-foreground/10 flex flex-wrap items-center gap-2">
        <button
          onClick={handleConfirm}
          disabled={confirmMut.isPending || !hasPrice || isDenied}
          title={!hasPrice ? "Set a price before confirming" : undefined}
          className="rounded-full px-3 py-1.5 text-xs font-medium tracking-wide bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed [transition-property:background-color,color,transform] duration-200 active:scale-[0.96]"
        >
          {confirmMut.isPending
            ? isConfirmed ? "Resending…" : "Confirming…"
            : isConfirmed
              ? "Resend proposal"
              : "Confirm & invite"}
        </button>
        <button
          onClick={handleDeny}
          disabled={denyMut.isPending || isDenied || isConfirmed}
          className="rounded-full px-3 py-1.5 text-xs font-medium tracking-wide border border-foreground/15 text-foreground/70 hover:text-foreground hover:border-foreground/30 disabled:opacity-50 disabled:cursor-not-allowed [transition-property:color,border-color,transform] duration-200 active:scale-[0.96]"
        >
          {denyMut.isPending ? "Denying…" : isDenied ? "Denied" : "Deny"}
        </button>

        {!editingPrice ? (
          <button
            onClick={() => setEditingPrice(true)}
            className="rounded-full border border-foreground/15 px-3 py-1.5 text-xs font-medium text-foreground/70 hover:text-foreground hover:border-foreground/30 [transition-property:color,border-color,transform] duration-200 active:scale-[0.96]"
          >
            {lead.admin_price_cents != null ? "Edit price" : "Set price"}
          </button>
        ) : (
          <div className="flex items-center gap-1.5">
            <input
              type="number"
              min="0"
              step="0.01"
              value={priceInput}
              onChange={(e) => setPriceInput(e.target.value)}
              placeholder="2500"
              className="w-24 rounded-lg border border-foreground/15 bg-white px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-foreground/20"
            />
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="rounded-lg border border-foreground/15 bg-white px-2 py-1 text-xs focus:outline-none"
            >
              <option value="usd">USD</option>
              <option value="eur">EUR</option>
              <option value="gbp">GBP</option>
            </select>
            <button
              onClick={handleSavePrice}
              disabled={priceMut.isPending}
              className="rounded-full bg-foreground text-background px-3 py-1.5 text-xs font-medium hover:opacity-90 disabled:opacity-50"
            >
              {priceMut.isPending ? "Saving…" : "Save"}
            </button>
            <button
              onClick={() => {
                setEditingPrice(false);
                setPriceInput(
                  lead.admin_price_cents != null ? (lead.admin_price_cents / 100).toString() : "",
                );
                setCurrency(lead.admin_currency);
              }}
              className="text-xs text-foreground/50 hover:text-foreground px-2"
            >
              Cancel
            </button>
          </div>
        )}

        <div className="flex-1" />

        <button
          onClick={handleDelete}
          disabled={deleteMut.isPending}
          className="rounded-full border border-destructive/30 px-3 py-1.5 text-xs font-medium text-destructive hover:bg-destructive hover:text-white [transition-property:background-color,color,transform] duration-200 active:scale-[0.96] disabled:opacity-50"
        >
          {deleteMut.isPending ? "Deleting…" : "Delete"}
        </button>
      </div>

      {(confirmMut.isError || denyMut.isError || priceMut.isError || deleteMut.isError) && (
        <p className="mt-2 text-xs text-destructive">
          {((confirmMut.error || denyMut.error || priceMut.error || deleteMut.error) as Error)?.message}
        </p>
      )}
    </li>
  );
}

function Splash({ text, children }: { text?: string; children?: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="text-center">
        {text && (
          <p className="text-foreground/50 font-mono text-xs tracking-widest uppercase">{text}</p>
        )}
        {children}
      </div>
    </div>
  );
}

function Empty({ title, sub }: { title: string; sub: string }) {
  return (
    <div className="rounded-2xl border border-dashed border-foreground/15 p-12 text-center">
      <p className="font-display text-2xl tracking-tight text-foreground">{title}</p>
      <p className="mt-2 text-sm text-foreground/55">{sub}</p>
    </div>
  );
}
